import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Button, Modal } from 'antd';
import { I18n } from 'react-redux-i18n';
import styled from 'styled-components';
import { Marker, NavigationControl } from 'react-map-gl';
import { Map as ReactMap } from 'react-map-gl';
import { WebMercatorViewport } from '@math.gl/web-mercator';
import 'mapbox-gl/dist/mapbox-gl.css';
import { debounce } from 'lodash';
import moment from 'moment';

import Icon from 'components/Icon';
import Pellet from 'components/Pellet';
import Select from 'components/Select/SelectFilter';
import RadioSwitch from 'components/RadioSwitch';
import ViewContainer from 'modules/Travel/components/Map/container';
import PointsOfSellsList from 'modules/Travel/components/PointsOfSellsList';
import DestinationPoint from 'modules/Travel/components/DestinationPoint';

import { getColorToStatus, getOption, getStateToStatus } from 'utils/pos';

const MOBILE_BREAKPOINT = 845;
const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;

const MAPBOX_TOKEN =
  'pk.eyJ1IjoiZGMtYW50b2luZSIsImEiOiJjanM1cXNpNzAwZ3ZoM3lzYjZvNjQ3dTJoIn0.BVKVyGXmYOqgvZqDWcMKXA';

// Types
type Option = {
  label: string;
  value: string | number;
  name?: string;
};

type OptionWithId = {
  label: string;
  value: number;
  name?: string;
};

type POSMarker = {
  store_id: number;
  name: string;
  latitude?: number | null;
  longitude?: number | null;
  network_code: string;
  state: string;
  isHotel?: boolean;
  dn_total?: number;
};

type POSRefData = POSMarker & {
  client_refs_by_bottle?: any[];
  rival_wines?: Record<string, any>;
  rivals_wines_by_brand?: Record<string, any>;
  client_wines_by_brand?: any[];
  client_refs_by_wine?: any[];
  ws_award?: string;
  stars?: number;
  phone?: string;
  email?: string;
  address?: string;
  store_type?: string;
  site?: string;
  photo?: string;
};

type Market = {
  market_id: number;
  market_name: string;
  on_year?: number | null;
  off_year?: number | null;
  on_month?: number | null;
  off_month?: number | null;
  on_continuous_mode?: boolean | null;
  on_campaign_market_presentation_pdf?: string | null;
  off_campaign_market_presentation_pdf?: string | null;
  market_human_validation?: boolean | null;
};

type PillOption = {
  label: string | React.ReactNode;
  value: string;
  active: boolean;
};

type MapProps = {
  market: any;
  markers: any[];
  optionsMarket: any[];
  posRefsData: any[];
  onChangeMarket: (value: any) => void;
  hasUSCampaigns: boolean;
  isOnlyUS: boolean;
  selectUSCampaign: boolean;
  onChangeUSCampaign: (value: boolean) => void;
  wines: any[];
  optionsWine: any[];
  onChangeWines: (values: any) => void;
  isBranded: boolean;
  hasWineshopAccess: boolean;
  productType?: string;
};

type ViewState = {
  latitude: number;
  longitude: number;
  zoom: number;
  pitch?: number;
  bearing?: number;
  transitionDuration?: number | 'auto';
};

// Styled Components
const PillPos = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  p {
    margin-left: 4px;
    white-space: nowrap;
  }
`;

const MarkerContainer = styled.div<{ network_code: string; state: string }>`
  width: 16px;
  height: 16px;
  border-radius: ${(props) => (props.network_code === 'ON' ? '50%' : '0px')};
  background-color: ${(props) => getColorToStatus(props.state)};

  :hover {
    cursor: pointer;
  }
`;

// Point of Sales Modal/Panel Component
type PointOfSalesProps = {
  displayPos: boolean;
  openInfo: boolean;
  markerInfo: any;
  wines: any[];
  pills: any;
  filteredData: any[];
  hasWineshopAccess: boolean;
  search: string;
  isBranded: boolean;
  optionsWine: any[];
  optionsSearchable: any[];
  onChangeWines: (value: any) => void;
  selectMarker: (marker: any) => void;
  onChangeInput: (value: string) => void;
  onChangeStatusPill: (status: string) => void;
  onChangeTypePill: (type: string) => void;
  removePosId: (storeId: number) => void;
  resetExcludedPos: () => void;
  onCloseMarker: () => void;
  onCloseModal: () => void;
};

const PointOfSales = ({
  displayPos,
  openInfo,
  markerInfo,
  wines,
  pills,
  filteredData,
  hasWineshopAccess,
  search,
  isBranded,
  optionsWine,
  optionsSearchable,
  onChangeWines,
  selectMarker,
  onChangeInput,
  onChangeStatusPill,
  onChangeTypePill,
  removePosId,
  resetExcludedPos,
  onCloseMarker,
  onCloseModal
}: PointOfSalesProps) => {
  const DestinationPointComponent = markerInfo && (
    <DestinationPoint
      {...(markerInfo as any)}
      isBranded={isBranded}
      handleCloseMarker={onCloseMarker}
    />
  );

  const PosListComponent = (
    <PointsOfSellsList
      wines={wines}
      optionsWine={optionsWine}
      hasWineshopAccess={hasWineshopAccess}
      optionsPills={pills}
      isBranded={isBranded}
      contentRow={filteredData}
      searchValue={search}
      searchable={optionsSearchable}
      onChangeWines={onChangeWines}
      resetExcluded={resetExcludedPos}
      handleChangeStatusPill={onChangeStatusPill}
      handleChangeTypePill={onChangeTypePill}
      removeRow={removePosId}
      handleInputChange={onChangeInput}
      handleSelectMarker={selectMarker}
    />
  );

  if (isMobile) {
    return (
      <Modal
        title={I18n.t('travel.posListModalTitle').toUpperCase()}
        open={displayPos || openInfo}
        closable={!(displayPos && openInfo)}
        onCancel={onCloseModal}
        style={{ bottom: 20 }}
        footer={<></>}
      >
        {openInfo ? DestinationPointComponent : PosListComponent}
      </Modal>
    );
  }

  return (
    <div className="infoContainer">
      {openInfo
        ? DestinationPointComponent
        : displayPos
          ? PosListComponent
          : null}
    </div>
  );
};

// Main Map Component
const Map = ({
  market,
  markers,
  optionsMarket,
  posRefsData,
  onChangeMarket,
  hasUSCampaigns,
  isOnlyUS,
  selectUSCampaign,
  onChangeUSCampaign,
  wines,
  optionsWine,
  onChangeWines,
  isBranded,
  hasWineshopAccess,
  productType
}: MapProps) => {
  // State
  const [viewState, setViewState] = useState<ViewState>({
    latitude: 46.227638,
    longitude: 2.213749,
    zoom: 5,
    pitch: 45
  });
  const [displayPos, setDisplayPos] = useState(!isMobile);
  const [activeStatusPill, setActiveStatusPill] = useState('all');
  const [activeTypePill, setActiveTypePill] = useState('all');
  const [search, setSearch] = useState('');
  const [openInfo, setOpenInfo] = useState(false);
  const [markerInfo, setMarkerInfo] = useState<POSRefData | null>(null);
  const [excludedPos, setExcludedPos] = useState<number[]>([]);

  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);

  // Memoized market value for select
  const marketValue = useMemo(() => {
    return getOption(market?.market_name);
  }, [market?.market_name]);

  // Memoized searchable options
  const searchableOptions = useMemo(() => {
    return markers
      .map((item) => getOption(item.name))
      .filter((opt): opt is Option => opt !== null);
  }, [markers]);

  // Filter data based on active filters
  const filteredData = useMemo(() => {
    let filtered = [...markers];

    // Filter markers with valid coordinates
    filtered = filtered.filter((item) => item.longitude && item.latitude);

    // Status filter
    if (activeStatusPill !== 'all') {
      filtered = filtered.filter((item) => item.state === activeStatusPill);
    }

    // Type filter
    if (activeTypePill !== 'all') {
      filtered = filtered.filter(
        (item) => item.network_code === activeTypePill
      );
    }

    // Excluded POS filter
    if (excludedPos.length > 0) {
      filtered = filtered.filter(
        (item) => !excludedPos.includes(item.store_id)
      );
    }

    // Search filter
    if (search && search.length > 0) {
      const regex = new RegExp(search, 'gi');
      filtered = filtered.filter((item) => regex.test(item.name));
    }

    return filtered;
  }, [markers, activeStatusPill, activeTypePill, excludedPos, search]);

  // Pills configuration
  const pills = useMemo(() => {
    // Default to 'wine' if productType is not provided
    const isSpirit = productType === 'spirit';
    return {
      status: [
        {
          label: I18n.t('placeholderAll'),
          value: 'all',
          active: activeStatusPill === 'all'
        },
        {
          label: (
            <PillPos>
              <Pellet
                value={getStateToStatus('pos') || undefined}
                color={undefined}
                square={undefined}
                size={undefined}
              />
              <p>{I18n.t('travel.statusOptions.pos')}</p>
            </PillPos>
          ),
          value: 'pos',
          active: activeStatusPill === 'pos'
        },
        {
          label: (
            <PillPos>
              <Pellet
                value={getStateToStatus('lost') || undefined}
                color={undefined}
                square={undefined}
                size={undefined}
              />
              <p>{I18n.t('travel.statusOptions.lost')}</p>
            </PillPos>
          ),
          value: 'lost',
          active: activeStatusPill === 'lost'
        },
        {
          label: (
            <PillPos>
              <Pellet
                value={getStateToStatus('todo') || undefined}
                color={undefined}
                square={undefined}
                size={undefined}
              />
              <p>{I18n.t('travel.statusOptions.conquer')}</p>
            </PillPos>
          ),
          value: 'todo',
          active: activeStatusPill === 'todo'
        },
        {
          label: (
            <PillPos>
              <Pellet
                value={undefined}
                color={undefined}
                square={undefined}
                size={undefined}
              />
              <p>{I18n.t('travel.statusOptions.other')}</p>
            </PillPos>
          ),
          value: 'other',
          active: activeStatusPill === 'other'
        }
      ],
      type: [
        {
          label: I18n.t('placeholderAll'),
          value: 'all',
          active: activeTypePill === 'all'
        },
        {
          label: (
            <PillPos>
              <Pellet
                value={undefined}
                color={undefined}
                square={undefined}
                size={undefined}
              />
              <p>
                {isSpirit
                  ? 'ON Trade'
                  : I18n.t('travel.typeOptions.restaurant')}
              </p>
            </PillPos>
          ),
          value: 'ON',
          active: activeTypePill === 'ON'
        },
        {
          label: (
            <PillPos>
              <Pellet
                value={undefined}
                color={undefined}
                square={true}
                size={undefined}
              />
              <p>
                {isSpirit ? 'OFF Trade' : I18n.t('travel.typeOptions.cave')}
              </p>
            </PillPos>
          ),
          value: 'OFF',
          active: activeTypePill === 'OFF'
        }
      ]
    };
  }, [activeStatusPill, activeTypePill, productType]);

  // Date report text
  const dateReport = useMemo(() => {
    if (!market || (!market.on_year && !market.off_year)) {
      return '';
    }

    const isSpirit = productType === 'spirit';
    const onLabel = isSpirit
      ? 'ON Trade'
      : I18n.t('restaurants&caves.networkOptions.restaurant');
    const offLabel = isSpirit
      ? 'OFF Trade'
      : I18n.t('restaurants&caves.networkOptions.cave');

    if (
      market.on_year === market.off_year &&
      market.off_month === market.on_month
    ) {
      if (market.on_continuous_mode) {
        return I18n.t('travel.realTime');
      }
      if (market.on_month) {
        return `${I18n.t('travel.date')} ${moment(
          market.on_month.toString()
        ).format('MMMM')} ${market.on_year}`;
      }
      return `${I18n.t('travel.date')} ${market.on_year}`;
    }

    let text = `${I18n.t('travel.date')}`;
    if (market.on_year && market.on_month) {
      if (market.on_continuous_mode) {
        text = I18n.t('travel.realTime') + ' (' + onLabel + ')';
      } else {
        text += ` ${moment(market.on_month.toString()).format('MMMM')} ${
          market.on_year
        } (${onLabel})`;
      }
    }
    if (
      market.on_year &&
      market.on_month &&
      market.off_year &&
      market.off_month
    ) {
      text += ` ${I18n.t('and')} `;
    }
    if (market.off_year && market.off_month) {
      text += ` ${moment(market.off_month.toString()).format('MMMM')} ${
        market.off_year
      } (${offLabel})`;
    }

    return text;
  }, [market, productType]);

  // Sorted filtered data for display
  const sortedFilteredData = useMemo(() => {
    return [...filteredData].sort((a, b) =>
      String(a.name).localeCompare(String(b.name))
    );
  }, [filteredData]);

  // Event Handlers
  const handleInputChange = useCallback(
    (value: string) => {
      if (value || search.length === 1) {
        setSearch(value);
      }
    },
    [search]
  );

  const handleCloseMarker = useCallback(() => {
    setOpenInfo(false);
    setMarkerInfo(null);
  }, []);

  const handleOpenMarker = useCallback(
    (marker: POSMarker, event?: React.MouseEvent) => {
      // Prevent event from bubbling to map click handler
      if (event) {
        event.stopPropagation();
      }

      const posRefs = posRefsData.filter(
        (item) => item.store_id === marker.store_id
      );
      const posRef = posRefs.length > 0 ? posRefs[0] : {};

      const fullPosDetails: POSRefData = {
        ...marker,
        ...posRef
      };

      setOpenInfo(true);
      setMarkerInfo(fullPosDetails);
    },
    [posRefsData]
  );

  const handleSelectMarker = useCallback((marker: POSMarker) => {
    setOpenInfo(true);
    setMarkerInfo(marker as POSRefData);
    if (marker.latitude && marker.longitude) {
      setViewState((prev) => ({
        ...prev,
        latitude: marker.latitude!,
        longitude: marker.longitude!,
        zoom: 16
      }));
    }
  }, []);

  const handleRemovePos = useCallback((storeId: number) => {
    setExcludedPos((prev) => [...prev, storeId]);
  }, []);

  const handleResetExcludedPos = useCallback(() => {
    setExcludedPos([]);
  }, []);

  const handleCloseModal = useCallback(() => {
    setOpenInfo(false);
    setDisplayPos(false);
  }, []);

  const handleMapClick = useCallback(() => {
    setOpenInfo(false);
  }, []);

  const handleToggleDisplayPos = useCallback(() => {
    setDisplayPos((prev) => !prev);
  }, []);

  // Effect: Fit bounds when markers change
  useEffect(() => {
    if (
      markers.length > 0 &&
      markers.some((m) => m.latitude && m.longitude) &&
      mapContainer.current
    ) {
      const validMarkers = markers.filter((m) => m.latitude && m.longitude);
      const maxLat = Math.max(...validMarkers.map((item) => item.latitude!));
      const maxLng = Math.max(...validMarkers.map((item) => item.longitude!));
      const minLat = Math.min(...validMarkers.map((item) => item.latitude!));
      const minLng = Math.min(...validMarkers.map((item) => item.longitude!));

      const { width, height } = mapContainer.current.getBoundingClientRect();
      const viewport = new WebMercatorViewport({
        width,
        height,
        latitude: viewState.latitude,
        longitude: viewState.longitude,
        zoom: viewState.zoom
      });

      const { longitude, latitude, zoom } = viewport.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat]
        ],
        { padding: 40 }
      );

      setViewState({
        latitude,
        longitude,
        zoom: zoom - 1,
        pitch: 45,
        transitionDuration: 5000
      });
    }
  }, [markers]);

  // Effect: Close marker info when market changes
  useEffect(() => {
    if (openInfo) {
      setOpenInfo(false);
      setMarkerInfo(null);
    }
  }, [market?.market_name]);

  // Effect: Handle window resize - Map component handles this automatically in v7
  useEffect(() => {
    const handleResize = debounce(() => {
      // Trigger re-render to update map bounds if needed
      if (mapRef.current) {
        mapRef.current.resize();
      }
    }, 100);

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, []);

  // Render Markers
  const renderMarkers = useMemo(() => {
    return filteredData.map((marker, index) => (
      <Marker
        key={`${marker.store_id}-${index}`}
        latitude={marker.latitude!}
        longitude={marker.longitude!}
      >
        {marker.isHotel ? (
          <div
            onClick={(e) => handleOpenMarker(marker, e)}
            className="hotelMarker"
          >
            <div className="hotelIconContainer">
              <Icon icon="bed" alt="Hotel" />
            </div>
            <div className="iconHotelTriangle" />
          </div>
        ) : (
          <MarkerContainer
            network_code={marker.network_code}
            state={marker.state}
            onClick={(e) => handleOpenMarker(marker, e)}
          />
        )}
      </Marker>
    ));
  }, [filteredData, handleOpenMarker]);

  // Render Overlay
  const renderOverlay = () => (
    <div className="overlayMap">
      <div className="overlayHeader">
        <div className="overlayTitleContainer">
          <h1 className="overlayTitle">{I18n.t('travel.title')}</h1>
          <div className="overlaySubTitleContainer">
            <h1 className="overlaySubtitle">
              {I18n.t('travel.cityInputLabel')}
            </h1>
            <div className="overlaySelectContainer">
              <div>
                <Select
                  placeholder="NEW YORK"
                  selectedOption={marketValue}
                  value={marketValue}
                  options={optionsMarket}
                  onChange={onChangeMarket}
                  components={{ ClearIndicator: null }}
                  containerStyles={{ zIndex: 10 }}
                  dropdownProps={{
                    fontSize: '14px',
                    colorIcon: '#E25D34',
                    styles: 'margin-left:-35px;margin-top:12px'
                  }}
                  customStyles={{
                    menu: {
                      zIndex: 11
                    },
                    placeholder: {
                      fontSize: isMobile ? 18 : 30
                    },
                    container: {
                      fontSize: isMobile ? 18 : 30,
                      backgroundColor: 'transparent',
                      width: isMobile ? 'calc(100vw - 16px)' : 400,
                      border: 0
                    },
                    control: {
                      border: 0,
                      backgroundColor: 'transparent'
                    },
                    valueContainer: {
                      borderBottom: '1px solid #B8BCC0',
                      fontFamily: 'Oswald',
                      marginLeft: 0,
                      padding: 0
                    },
                    singleValue: {
                      overflow: 'visible'
                    }
                  }}
                />
              </div>
              <div>
                {hasUSCampaigns && !isOnlyUS && (
                  <div>
                    <RadioSwitch
                      checked={selectUSCampaign}
                      onChange={onChangeUSCampaign}
                      label={I18n.t('restaurants&caves.filters.USmarket')}
                      disabled={!hasUSCampaigns}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="lockMapContainer">
            <Button
              className="button-display-pos"
              ghost={true}
              onClick={handleToggleDisplayPos}
            >
              {I18n.t('travel.showCityList')}
            </Button>
          </div>
        </div>
      </div>
      {dateReport && !isMobile && (
        <p className="dateReportText">{dateReport}</p>
      )}
      <PointOfSales
        displayPos={displayPos}
        openInfo={openInfo}
        markerInfo={markerInfo}
        wines={wines}
        pills={pills}
        filteredData={sortedFilteredData}
        hasWineshopAccess={hasWineshopAccess}
        search={search}
        isBranded={isBranded}
        optionsWine={optionsWine}
        optionsSearchable={searchableOptions}
        onChangeWines={onChangeWines}
        selectMarker={handleSelectMarker}
        onChangeInput={handleInputChange}
        onChangeStatusPill={setActiveStatusPill}
        onChangeTypePill={setActiveTypePill}
        removePosId={handleRemovePos}
        resetExcludedPos={handleResetExcludedPos}
        onCloseMarker={handleCloseMarker}
        onCloseModal={handleCloseModal}
      />
    </div>
  );

  return (
    <ViewContainer
      ref={mapContainer}
      style={{ position: 'relative', width: '100%', height: '100%' }}
      lockMap={false}
    >
      <ReactMap
        ref={mapRef}
        {...viewState}
        onMove={(evt) => setViewState(evt.viewState)}
        onClick={handleMapClick}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/light-v10"
        mapboxAccessToken={MAPBOX_TOKEN}
      >
        {renderMarkers}
        <NavigationControl position="bottom-right" showCompass={false} />
      </ReactMap>
      {renderOverlay()}
      <div className="whiteShadow" />
    </ViewContainer>
  );
};

export default Map;
